/**
 * PJ の一覧（`/pjs`）。変更一覧を見たい PJ をここから選ぶ。
 *
 * **PJ は期間で絞らずに全件引く（`listPjs`）。** 期間を当てると、それより前に触られた
 * PJ が一覧から静かに消える。消えるのは古い PJ で、その中には**出し終えたつもりで実は
 * 反映していない PJ**が混じる。見落としてはいけないものが最初に消える絞り込みは、この画面
 * には置けない。
 *
 * **列は 案件番号 ／ 案件名 ／ 状態 の 3 つだけにする**（2026-09-28 Dhik「PJ一覧：案件番号、案件名、
 * 状態とかにしてほしい」）。案件名は Backlog の課題の件名で、読取が 1 行に載せて返す（決定 U21）。
 * 件名が取れない案件は「—」を出す。長い件名は 1 行で省略し、`title` に全文を持たせる。状態は
 * 仕様への反映（未反映 ／ 反映済み）で、Backlog の課題の状態ではない。「未反映」のまま残っている
 * PJ が反映漏れそのものなので、一覧を見ただけでそれと分かる必要がある。
 *
 * **要求が 0 件の PJ には要確認の印 `!` を状態の欄の中に小さく付ける。** 作り直した後は要求が
 * 無いと仕様に書き込めないので、要求の無い案件はそのままでは進められない。理由の 1 文はホバーと
 * フォーカスで読めるようにする（`title` ・ `aria-label` ・ `tabIndex`）。
 *
 * **反映済みは既定で畳み、トグルで出す。** 一覧に用があるのは未反映の PJ だからである。
 * **見出しと表の行のほかは出さない**（Dhik 2026-09-29「本当に出すべき情報を 1 or 2 個に」）。件数は
 * 見出しの `title`、状態の札（未反映 ／ 反映済み）は行の `title` に載せる。既定は未反映だけで全行が
 * 同じ札になるためである。節の見出し「PJ」と、行が 0 件のときの案内の文は置かない — 見出しの
 * 「PJ 一覧」と、行が無く「反映済みも出す（N 件）」だけが見えることが同じことを言う。
 * 件数は PJ と 1 対 1 で青天井にならないので、全件を取って画面側で畳む。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ を案件番号・案件名の行で一覧し（件数と状態は title）、要求 0 件に要確認の印を付けて、PJ の変更一覧へ繋ぐ。
 * @graph-connects usePjs [calls] PJ 一覧の取得
 */
import type { ListPjsOutput } from '@cortex/domain-spec-console';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '../components/spec/fallback-states.js';
import { usePjs } from '../hooks/queries/spec-graph-element-pj-queries.js';
import { formatPjLabel } from '../lib/pj-label.js';
import { PJ_DIFF_ROUTE } from './route-paths.js';

/** 一覧の 1 行。 @graph-connects none */
type PjRow = ListPjsOutput['pjs'][number];

/** 表の列の並び（見出し行と各行で同じ値を使う）。 @graph-connects none */
const ROW_GRID = 'grid grid-cols-[9rem_minmax(0,1fr)_8rem] items-center gap-3 px-2 py-2';

/**
 * 行を最終更新の新しい順に並べる。同時刻は案件番号の大きい順（新しい案件が先）。
 *
 * @param rows 一覧の行
 * @returns 並べ替えた行
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ の一覧を最終更新の新しい順に並べる。
 * @graph-connects none
 */
export function sortPjRows(rows: readonly PjRow[]): readonly PjRow[] {
  return [...rows].sort(
    (left, right) =>
      right.updatedAt.localeCompare(left.updatedAt) || right.pjNumber - left.pjNumber,
  );
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ の一覧を、取得中・失敗・本体で描き、行が無ければ表を置かない。
 * @graph-connects usePjs [calls] PJ 一覧の取得
 * @graph-connects formatPjLabel [calls] 案件番号の画面表記
 */
export function PjsPage() {
  const { t } = useTranslation();
  const pjs = usePjs();
  /*
   * 絞り込みは画面の中の状態に持ち、URL には出さない。PJ 一覧は PJ を選ぶ入口で、深いリンクで
   * 誰かへ渡す対象ではない。
   */
  const [showMerged, setShowMerged] = useState(false);

  if (pjs.error !== null && pjs.error !== undefined) {
    return <ErrorState error={pjs.error} />;
  }

  if (pjs.isLoading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="4rem" />
        <Skeleton variant="rectangular" height="12rem" />
      </div>
    );
  }

  const all = sortPjRows(pjs.data?.pjs ?? []);
  const mergedCount = all.filter((row) => row.state !== 'open').length;
  const rows = showMerged ? all : all.filter((row) => row.state === 'open');

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      <header className="flex flex-wrap items-center gap-3">
        <h1
          data-pjs-shown={rows.length}
          title={t('pjs.count', { count: rows.length })}
          className="text-2xl font-bold text-[var(--color-text-primary)]"
        >
          {t('pjs.title')}
        </h1>
        {/*
          反映済みは既定で畳む。件数を横に出すのは、畳まれている側に何件あるかが見えないと
          「隠れているのか、そもそも無いのか」が区別できないためである。
        */}
        <label className="ml-auto flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            checked={showMerged}
            onChange={(event) => {
              setShowMerged(event.target.checked);
            }}
            className="h-4 w-4 accent-[var(--color-accent-primary)]"
          />
          {t('pjs.showMerged', { count: mergedCount })}
        </label>
      </header>

      {rows.length > 0 && (
        <Card padding="md">
          <div className="flex flex-col text-sm">
            <div
              className={`${ROW_GRID} border-b border-[var(--color-border)] text-xs text-[var(--color-text-muted)]`}
            >
              <span>{t('pjs.colPjNumber')}</span>
              <span>{t('pjs.colName')}</span>
              <span>{t('pjs.colState')}</span>
            </div>
            <ul className="flex flex-col">
              {rows.map((row) => (
                <li key={row.pjNumber}>
                  <PjListRow row={row} />
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}
    </div>
  );
}

/** `PjListRow` の props。 @graph-connects none */
interface PjListRowProps {
  row: PjRow;
}

/**
 * 一覧の 1 行。行全体が変更一覧へのリンクである。
 *
 * 行き先のパスパラメータは案件番号の綴りそのもの（`139162`）である。画面に出す前置き
 * （`AIRCLOSET-`）を URL へ持ち込むと、受け口の関門（1 以上の整数）を通らず未存在として弾かれる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ 1 件を案件番号・案件名の 1 行で描き（状態は行の title）、要求 0 件に要確認の印を状態の欄の中に付ける。
 * @graph-connects formatPjLabel [calls] 案件番号の画面表記
 */
function PjListRow({ row }: PjListRowProps) {
  const { t } = useTranslation();
  const open = row.state === 'open';

  return (
    <Link
      to={PJ_DIFF_ROUTE}
      params={{ pjNumber: String(row.pjNumber) }}
      data-pj-row={row.pjNumber}
      title={open ? t('pjs.stateOpen') : t('pjs.stateMerged')}
      className={`${ROW_GRID} border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]`}
    >
      <span className="font-mono text-[var(--color-accent-primary)] underline">
        {formatPjLabel(row.pjNumber)}
      </span>
      <span
        className="truncate text-[var(--color-text-primary)]"
        title={row.name ?? undefined}
        data-pj-name={row.name ?? ''}
      >
        {row.name ?? '—'}
      </span>
      <span className="flex items-center gap-1.5" data-pj-state={row.state}>
        {row.requirementCount === 0 && (
          <span
            tabIndex={0}
            title={t('pjs.noRequirement')}
            aria-label={t('pjs.noRequirement')}
            data-pj-needs-check="true"
            className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[var(--color-error)] text-[10px] font-bold text-[var(--color-error)]"
          >
            !
          </span>
        )}
      </span>
    </Link>
  );
}
